import { useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  KeyValue,
  Section,
  Select,
  Spinner,
  Switch,
} from '@zt/ui';
import { api, newIdempotencyKey } from '../../lib/api';
import { HelpTip, PageFrame } from '../../lib/help';
import { useI18n, useT } from '../../lib/i18n';
import { fieldError, useApiMutation } from '../../lib/mutation';
import { usePermissions } from '../../lib/session';
import { microsToInput, usdToMicros, useUsd } from './shared';

/**
 * Yapay zekâ ayarları (F9-01, ADR-126): açık onayla açma, aylık harcama sınırı, modeller, fiyat tablosu, kullanım
 * raporu. Anahtarlar Entegrasyonlar ekranında; burada anahtar gösterilmez.
 */

interface ProviderStatus {
  status: 'NOT_CONFIGURED' | 'SANDBOX' | 'LIVE' | 'ERROR' | 'DISABLED';
  lastTestedAt: string | null;
  lastMessage: string | null;
}
interface PriceRow {
  model: string;
  kind: 'text' | 'image';
  inputPerMTok: number | null;
  outputPerMTok: number | null;
  custom: boolean;
  known: boolean;
}
interface Overview {
  settings: {
    enabled: boolean;
    enabledAt: string | null;
    enabledByName: string | null;
    monthlyLimitCents: number;
    dailyRequestLimit: number;
    textModel: string;
    lightModel: string;
    imageModel: string;
    imageQuality: 'low' | 'medium' | 'high';
    imageReserveMicros: number;
    supportDrafts: boolean;
  };
  providers: { text: ProviderStatus; image: ProviderStatus };
  prices: PriceRow[];
  pricesVerifiedAt: string;
  month: {
    spentMicros: number;
    spentCents: number;
    remainingCents: number;
    usedPercent: number;
    requests: number;
    requestsToday: number;
  };
}
interface UsageRow {
  id: string;
  createdAt: string;
  purpose: string;
  provider: 'anthropic' | 'openai';
  model: string;
  status: 'pending' | 'ok' | 'error';
  errorCode: string | null;
  inputTokens: number;
  outputTokens: number;
  images: number;
  costMicros: number;
  maskedCount: number;
  claimFindings: number;
  userName: string | null;
}
interface Usage {
  month: string;
  totals: {
    requests: number;
    ok: number;
    errors: number;
    pending: number;
    costMicros: number;
    inputTokens: number;
    outputTokens: number;
    images: number;
    maskedCount: number;
  };
  byPurpose: Array<{
    purpose: string;
    requests: number;
    costMicros: number;
    inputTokens: number;
    outputTokens: number;
  }>;
  recent: UsageRow[];
}

const STATUS_TONE = {
  NOT_CONFIGURED: 'slate',
  SANDBOX: 'amber',
  LIVE: 'moss',
  ERROR: 'rose',
  DISABLED: 'slate',
} as const;

export function AiSettingsPage() {
  const t = useT();
  const { formatDate, formatNumber } = useI18n();
  const { can } = usePermissions();
  const canManage = can('ai.settings.manage');
  const usd = useUsd();
  const q = useQuery({
    queryKey: ['ai', 'overview'],
    queryFn: () => api.get<Overview>('/v1/admin/ai/overview'),
  });
  const [enableOpen, setEnableOpen] = useState(false);
  const [priceEdit, setPriceEdit] = useState<PriceRow | null>(null);
  const disable = useApiMutation(() => api.post('/v1/admin/ai/disable'), {
    invalidate: [['ai']],
    success: t('ai.settings.disabled'),
  });
  const o = q.data;
  if (q.isLoading || !o)
    return (
      <PageFrame helpKey="page.ai_settings">
        <Spinner label={t('common.loading')} />
      </PageFrame>
    );
  const s = o.settings;
  const provider = (label: string, p: ProviderStatus) => ({
    label,
    value: (
      <span className="inline-flex flex-wrap items-center gap-2">
        <Badge tone={STATUS_TONE[p.status]}>{t(`integrationStatus.${p.status}`)}</Badge>
        {p.lastMessage && p.status !== 'LIVE' && (
          <span className="text-sm text-ink-3">{t(`integrationMessages.${p.lastMessage}`)}</span>
        )}
        {p.status !== 'LIVE' && can('org.integration.read') && (
          <Link to="/entegrasyonlar" className="text-sm underline underline-offset-2">
            {t('ai.settings.toIntegrations')}
          </Link>
        )}
      </span>
    ),
  });
  return (
    <PageFrame helpKey="page.ai_settings">
      <Section
        title={t('ai.settings.statusTitle')}
        description={t('ai.settings.statusLead')}
        action={
          canManage && (
            <span className="inline-flex items-center gap-1">
              {s.enabled ? (
                <Button
                  size="sm"
                  variant="danger"
                  loading={disable.isPending}
                  onClick={() => disable.mutate(undefined)}
                >
                  {t('ai.settings.disable')}
                </Button>
              ) : (
                <Button size="sm" variant="primary" onClick={() => setEnableOpen(true)}>
                  {t('ai.settings.enable')}
                </Button>
              )}
              <HelpTip k={s.enabled ? 'ai.disable' : 'ai.enable'} />
            </span>
          )
        }
      >
        <KeyValue
          items={[
            {
              label: t('ai.settings.statusTitle'),
              value: (
                <span className="inline-flex flex-wrap items-center gap-2">
                  <Badge tone={s.enabled ? 'moss' : 'slate'}>
                    {s.enabled ? t('ai.settings.on') : t('ai.settings.off')}
                  </Badge>
                  {s.enabled && s.enabledAt && (
                    <span className="text-sm text-ink-3">
                      {t('ai.settings.enabledBy', {
                        name: s.enabledByName ?? '—',
                        date: formatDate(s.enabledAt),
                      })}
                    </span>
                  )}
                </span>
              ),
            },
            provider(t('ai.settings.textProvider'), o.providers.text),
            provider(t('ai.settings.imageProvider'), o.providers.image),
          ]}
        />
      </Section>

      <Section
        title={t('ai.settings.monthTitle')}
        description={t('ai.settings.estimateNote')}
        action={<HelpTip k="ai.limits" />}
      >
        <div className="space-y-3">
          <p className="text-[0.9375rem]">
            <span className="text-ink-3">{t('ai.settings.spent')}: </span>
            <span className="font-medium tabular-nums">
              {s.monthlyLimitCents > 0
                ? t('ai.settings.spentOf', {
                    spent: usd(o.month.spentMicros),
                    limit: usd(s.monthlyLimitCents * 10_000),
                  })
                : `${usd(o.month.spentMicros)} · ${t('ai.settings.noLimit')}`}
            </span>
          </p>
          {s.monthlyLimitCents > 0 && (
            <div
              className="h-2 w-full overflow-hidden rounded-full bg-surface-2"
              role="progressbar"
              aria-label={t('ai.settings.spent')}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={o.month.usedPercent}
            >
              <div
                className={o.month.usedPercent >= 90 ? 'h-full bg-amber' : 'h-full bg-pine'}
                style={{ width: `${o.month.usedPercent}%` }}
              />
            </div>
          )}
          <p className="text-sm text-ink-2">
            {t('ai.settings.requests')}: {formatNumber(o.month.requests)} ·{' '}
            {t('ai.settings.requestsToday', { count: o.month.requestsToday, limit: s.dailyRequestLimit })}
          </p>
        </div>
      </Section>

      {canManage && (
        // Sunucudaki ayar değişince (aç, fiyat, kaydet) form yeniden kurulur.
        <ConfigSection
          key={[
            s.monthlyLimitCents,
            s.dailyRequestLimit,
            s.textModel,
            s.lightModel,
            s.imageModel,
            s.imageQuality,
            s.imageReserveMicros,
            s.supportDrafts,
          ].join('|')}
          o={o}
        />
      )}

      <Section
        title={t('ai.settings.pricesTitle')}
        description={t('ai.settings.pricesLead', {
          date: formatDate(`${o.pricesVerifiedAt}T12:00:00Z`, false),
        })}
        action={<HelpTip k="ai.prices" />}
      >
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full min-w-[36rem] text-sm">
            <caption className="sr-only">{t('ai.settings.pricesTitle')}</caption>
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t('ai.settings.model')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('ai.settings.kind')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('ai.settings.inputPrice')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('ai.settings.outputPrice')}</th>
                {canManage && <th className="px-3 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {o.prices.map((p) => (
                <tr key={`${p.kind}-${p.model}`}>
                  <td className="px-3 py-2">
                    <span className="inline-flex flex-wrap items-center gap-2">
                      <code className="text-[0.8125rem]">{p.model}</code>
                      {p.custom && <Badge tone="amber">{t('ai.settings.custom')}</Badge>}
                    </span>
                  </td>
                  <td className="px-3 py-2">{t(`ai.settings.kinds.${p.kind}`)}</td>
                  <td className="px-3 py-2 text-end tabular-nums">
                    {p.inputPerMTok === null ? t('ai.settings.unknownPrice') : usd(p.inputPerMTok)}
                  </td>
                  <td className="px-3 py-2 text-end tabular-nums">
                    {p.outputPerMTok === null ? t('ai.settings.unknownPrice') : usd(p.outputPerMTok)}
                  </td>
                  {canManage && (
                    <td className="px-3 py-2 text-end">
                      <Button size="sm" variant="ghost" onClick={() => setPriceEdit(p)}>
                        {t('ai.settings.editPrice')}
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <UsageSection />

      {enableOpen && <EnableDialog onClose={() => setEnableOpen(false)} initialCents={s.monthlyLimitCents} />}
      {priceEdit && <PriceDialog row={priceEdit} onClose={() => setPriceEdit(null)} />}
    </PageFrame>
  );
}

function EnableDialog({ onClose, initialCents }: { onClose: () => void; initialCents: number }) {
  const t = useT();
  const { locale } = useI18n();
  const [limit, setLimit] = useState(initialCents > 0 ? microsToInput(initialCents * 10_000, locale) : '20');
  const [ack, setAck] = useState(false);
  const ackId = useId();
  const micros = usdToMicros(limit);
  const enable = useApiMutation(
    (vars: { cents: number }) =>
      api.post(
        '/v1/admin/ai/enable',
        { acknowledge: true, monthlyLimitCents: vars.cents },
        { idempotencyKey: newIdempotencyKey() },
      ),
    { invalidate: [['ai']], success: t('ai.settings.enabled'), onSuccess: onClose },
  );
  const cents = micros === null ? null : Math.floor(micros / 10_000);
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('ai.settings.enableTitle')}
      description={t('ai.settings.enableLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={!ack || cents === null || cents < 100}
            loading={enable.isPending}
            onClick={() => cents !== null && enable.mutate({ cents })}
          >
            {t('ai.settings.enableSubmit')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label={t('ai.settings.enableLimit')}
          description={t('ai.settings.enableLimitHint')}
          error={fieldError(enable.error, 'monthlyLimitCents')}
          required
        >
          {(p) => (
            <Input {...p} inputMode="decimal" value={limit} onChange={(e) => setLimit(e.target.value)} />
          )}
        </Field>
        <label htmlFor={ackId} className="flex items-start gap-2 text-sm">
          <input
            id={ackId}
            type="checkbox"
            className="mt-0.5 h-4 w-4"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
          />
          <span>{t('ai.settings.enableAck')}</span>
        </label>
        <p className="rounded-sm border border-amber/30 bg-amber-tint px-3 py-2 text-sm">
          {t('ai.settings.enableProviderNote')}
        </p>
      </div>
    </Dialog>
  );
}

function ConfigSection({ o }: { o: Overview }) {
  const t = useT();
  const { locale } = useI18n();
  const s = o.settings;
  const textModels = o.prices.filter((p) => p.kind === 'text').map((p) => p.model);
  const imageModels = o.prices.filter((p) => p.kind === 'image').map((p) => p.model);
  const [form, setForm] = useState({
    monthlyLimit: s.monthlyLimitCents > 0 ? microsToInput(s.monthlyLimitCents * 10_000, locale) : '',
    dailyRequestLimit: String(s.dailyRequestLimit),
    textModel: s.textModel,
    lightModel: s.lightModel,
    imageModel: s.imageModel,
    imageQuality: s.imageQuality,
    imageReserve: microsToInput(s.imageReserveMicros, locale),
    supportDrafts: s.supportDrafts,
  });
  const save = useApiMutation((body: Record<string, unknown>) => api.put('/v1/admin/ai/settings', body), {
    invalidate: [['ai']],
    success: t('ai.settings.saved'),
  });
  const limitMicros = usdToMicros(form.monthlyLimit);
  const reserveMicros = usdToMicros(form.imageReserve);
  const submit = () =>
    save.mutate({
      ...(form.monthlyLimit.trim() && limitMicros !== null
        ? { monthlyLimitCents: Math.floor(limitMicros / 10_000) }
        : {}),
      dailyRequestLimit: Number(form.dailyRequestLimit),
      textModel: form.textModel,
      lightModel: form.lightModel,
      imageModel: form.imageModel,
      imageQuality: form.imageQuality,
      ...(reserveMicros !== null ? { imageReserveMicros: reserveMicros } : {}),
      supportDrafts: form.supportDrafts,
    });
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));
  const err = (f: string) => fieldError(save.error, f);
  return (
    <Section title={t('ai.settings.configTitle')} action={<HelpTip k="ai.models" />}>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label={t('ai.settings.monthlyLimit')}
          error={err('monthlyLimitCents')}
          help={<HelpTip k="ai.limits" />}
        >
          {(p) => (
            <Input
              {...p}
              inputMode="decimal"
              value={form.monthlyLimit}
              onChange={(e) => set('monthlyLimit', e.target.value)}
            />
          )}
        </Field>
        <Field label={t('ai.settings.dailyLimit')} error={err('dailyRequestLimit')}>
          {(p) => (
            <Input
              {...p}
              type="number"
              min={1}
              max={5000}
              value={form.dailyRequestLimit}
              onChange={(e) => set('dailyRequestLimit', e.target.value)}
            />
          )}
        </Field>
        <Field label={t('ai.settings.textModel')} error={err('textModel')}>
          {(p) => (
            <Select {...p} value={form.textModel} onChange={(e) => set('textModel', e.target.value)}>
              {textModels.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label={t('ai.settings.lightModel')}
          description={t('ai.settings.lightModelHint')}
          error={err('lightModel')}
        >
          {(p) => (
            <Select {...p} value={form.lightModel} onChange={(e) => set('lightModel', e.target.value)}>
              {textModels.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('ai.settings.imageModel')} error={err('imageModel')}>
          {(p) => (
            <Select {...p} value={form.imageModel} onChange={(e) => set('imageModel', e.target.value)}>
              {imageModels.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('ai.settings.imageQuality')}>
          {(p) => (
            <Select
              {...p}
              value={form.imageQuality}
              onChange={(e) => set('imageQuality', e.target.value as typeof form.imageQuality)}
            >
              {(['low', 'medium', 'high'] as const).map((k) => (
                <option key={k} value={k}>
                  {t(`ai.settings.imageQualities.${k}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label={t('ai.settings.imageReserve')}
          description={t('ai.settings.imageReserveHint')}
          error={err('imageReserveMicros')}
        >
          {(p) => (
            <Input
              {...p}
              inputMode="decimal"
              value={form.imageReserve}
              onChange={(e) => set('imageReserve', e.target.value)}
            />
          )}
        </Field>
      </div>
      <div className="mt-5 flex items-start gap-3 rounded-md border border-line px-4 py-3">
        <Switch
          checked={form.supportDrafts}
          onCheckedChange={(v) => set('supportDrafts', v)}
          label={t('ai.settings.supportDrafts')}
        />
        <div className="text-sm">
          <p className="inline-flex items-center gap-1 font-medium">
            {t('ai.settings.supportDrafts')} <HelpTip k="ai.supportDrafts" />
          </p>
          <p className="text-ink-3">{t('ai.settings.supportDraftsHint')}</p>
        </div>
      </div>
      <div className="mt-4">
        <Button variant="primary" loading={save.isPending} onClick={submit}>
          {t('ai.settings.save')}
        </Button>
      </div>
    </Section>
  );
}

function PriceDialog({ row, onClose }: { row: PriceRow; onClose: () => void }) {
  const t = useT();
  const { locale } = useI18n();
  const toInput = (v: number | null) => microsToInput(v, locale);
  const [input, setInput] = useState(toInput(row.inputPerMTok));
  const [output, setOutput] = useState(toInput(row.outputPerMTok));
  const save = useApiMutation((body: Record<string, unknown>) => api.put('/v1/admin/ai/settings', body), {
    invalidate: [['ai']],
    success: t('ai.settings.priceSaved'),
    onSuccess: onClose,
  });
  const i = usdToMicros(input);
  const o = usdToMicros(output);
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('ai.settings.priceTitle', { model: row.model })}
      closeLabel={t('common.close')}
      footer={
        <>
          {row.custom && row.known && (
            <Button
              variant="ghost"
              loading={save.isPending}
              onClick={() => save.mutate({ prices: { [row.model]: null } })}
            >
              {t('ai.settings.resetPrice')}
            </Button>
          )}
          <Button
            variant="primary"
            disabled={i === null || o === null}
            loading={save.isPending}
            onClick={() => save.mutate({ prices: { [row.model]: { inputPerMTok: i, outputPerMTok: o } } })}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={`${t('ai.settings.inputPrice')} (USD)`}>
          {(p) => (
            <Input {...p} inputMode="decimal" value={input} onChange={(e) => setInput(e.target.value)} />
          )}
        </Field>
        <Field label={`${t('ai.settings.outputPrice')} (USD)`}>
          {(p) => (
            <Input {...p} inputMode="decimal" value={output} onChange={(e) => setOutput(e.target.value)} />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

function UsageSection() {
  const t = useT();
  const { formatDate, formatNumber } = useI18n();
  const usd = useUsd();
  const [month, setMonth] = useState('');
  const q = useQuery({
    queryKey: ['ai', 'usage', month],
    queryFn: () => api.get<Usage>(`/v1/admin/ai/usage${month ? `?month=${month}` : ''}`),
  });
  const u = q.data;
  return (
    <Section
      title={t('ai.settings.usageTitle')}
      description={t('ai.settings.usageLead')}
      action={<HelpTip k="ai.usage" />}
    >
      <div className="mb-4 max-w-xs">
        <Field label={t('ai.settings.month')}>
          {(p) => (
            <Input
              {...p}
              type="month"
              value={month || u?.month || ''}
              onChange={(e) => setMonth(e.target.value)}
            />
          )}
        </Field>
      </div>
      {!u ? (
        <Spinner label={t('common.loading')} />
      ) : u.totals.requests === 0 ? (
        <EmptyState title={t('ai.settings.noUsage')} />
      ) : (
        <div className="space-y-6">
          <p className="text-[0.9375rem]">
            {t('ai.settings.totals', {
              requests: formatNumber(u.totals.requests),
              ok: formatNumber(u.totals.ok),
              errors: formatNumber(u.totals.errors),
              cost: usd(u.totals.costMicros),
            })}
            <span className="block text-sm text-ink-3">
              {t('ai.settings.tokens', {
                input: formatNumber(u.totals.inputTokens),
                output: formatNumber(u.totals.outputTokens),
              })}
            </span>
          </p>
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full min-w-[30rem] text-sm">
              <caption className="sr-only">{t('ai.settings.byPurpose')}</caption>
              <thead className="bg-surface-2 text-ink-2">
                <tr>
                  <th className="px-3 py-2 text-start font-medium">{t('ai.settings.purpose')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('ai.settings.requests')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('ai.settings.cost')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {u.byPurpose.map((p) => (
                  <tr key={p.purpose}>
                    <td className="px-3 py-2">{t(`ai.purposes.${p.purpose}`)}</td>
                    <td className="px-3 py-2 text-end tabular-nums">{formatNumber(p.requests)}</td>
                    <td className="px-3 py-2 text-end tabular-nums">{usd(p.costMicros)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full min-w-[52rem] text-sm">
              <caption className="px-3 py-2 text-start font-medium">{t('ai.settings.recent')}</caption>
              <thead className="bg-surface-2 text-ink-2">
                <tr>
                  <th className="px-3 py-2 text-start font-medium">{t('ai.settings.when')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('ai.settings.purpose')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('ai.settings.model')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('ai.settings.status')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('ai.settings.cost')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('ai.settings.masked')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('ai.settings.claims')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('ai.settings.user')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {u.recent.map((r) => (
                  <tr key={r.id}>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.createdAt)}</td>
                    <td className="px-3 py-2">{t(`ai.purposes.${r.purpose}`)}</td>
                    <td className="px-3 py-2">
                      <code className="text-[0.8125rem]">{r.model}</code>
                    </td>
                    <td className="px-3 py-2">
                      <Badge tone={r.status === 'ok' ? 'moss' : r.status === 'error' ? 'rose' : 'amber'}>
                        {t(`ai.requestStatus.${r.status}`)}
                      </Badge>
                      {r.errorCode && <span className="block text-ink-3">{t(`errors.${r.errorCode}`)}</span>}
                    </td>
                    <td className="px-3 py-2 text-end tabular-nums">{usd(r.costMicros)}</td>
                    <td className="px-3 py-2 text-end tabular-nums">{r.maskedCount}</td>
                    <td className="px-3 py-2 text-end tabular-nums">{r.claimFindings}</td>
                    <td className="px-3 py-2">{r.userName ?? t('ai.settings.system')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Section>
  );
}
